import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Textarea, Checkbox, Box, Text, HStack, Tag, Alert, AlertIcon,
  useToast, Spinner,
} from '@chakra-ui/react';
import { ImmCita, ImmSesion, obtenerCita, registrarSesion } from '../../../api/immApi';
import { SectionTitle, IMM_COLOR, IMM_COLOR_HOVER, fmtDateTime } from '../_shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  cita: ImmCita;
  onGuardado?: () => void;
}

const ModalSesion: React.FC<Props> = ({ isOpen, onClose, cita, onGuardado }) => {
  const toast = useToast();
  const [notas, setNotas] = useState('');
  const [confidencial, setConfidencial] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [sesiones, setSesiones] = useState<ImmSesion[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setNotas('');
    setConfidencial(true);
    setCargando(true);
    obtenerCita(cita.id)
      .then((c) => setSesiones(c.sesiones || []))
      .catch(() => setSesiones([]))
      .finally(() => setCargando(false));
  }, [isOpen, cita.id]);

  const handleGuardar = async () => {
    if (!notas.trim()) {
      toast({ title: 'Escribe las notas de seguimiento', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      await registrarSesion(cita.id, { notasSeguimiento: notas, confidencial });
      toast({ title: 'Sesión registrada', status: 'success', duration: 3000 });
      onGuardado?.();
      onClose();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo registrar la sesión.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>
          Registrar sesión
        </ModalHeader>
        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            {cargando ? (
              <HStack justify="center" py={4}><Spinner color={IMM_COLOR} /></HStack>
            ) : sesiones.length > 0 && (
              <>
                <SectionTitle>Sesiones anteriores</SectionTitle>
                <VStack spacing={2} align="stretch">
                  {sesiones.map((s) => (
                    <Box key={s.id} borderWidth="1px" borderRadius="md" p={3} borderLeft={`3px solid ${IMM_COLOR}`}>
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" color="gray.500">{fmtDateTime(s.createdAt)}</Text>
                        {s.confidencial && <Tag size="sm" colorScheme="red" variant="subtle">Confidencial</Tag>}
                      </HStack>
                      <Text fontSize="sm" whiteSpace="pre-wrap">{s.notasSeguimiento || '—'}</Text>
                    </Box>
                  ))}
                </VStack>
              </>
            )}

            <SectionTitle>Nueva sesión</SectionTitle>
            <Alert status="info" fontSize="xs" borderRadius="md">
              <AlertIcon />
              Las notas de seguimiento son confidenciales y no se muestran en los listados.
            </Alert>
            <Textarea
              rows={5}
              placeholder="Notas de seguimiento de la sesión..."
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
            <Checkbox isChecked={confidencial} onChange={(e) => setConfidencial(e.target.checked)} colorScheme="red">
              Marcar como confidencial
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={guardando}>Cancelar</Button>
          <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
            Guardar sesión
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalSesion;
